/**
 * useMosaicKeyboard.ts
 */
import Event from '@shared/classes/event';
import Focus from '@shared/types/enums/focus';
import Routes from '@shared/types/enums/routes';
import Setting from '@shared/classes/setting';
import useKeyboard from '@shared/components/utils/hooks/useKeyboard';
import { Coordinates } from '@shared/utils/floatingMenu';
import { Dispatch, MutableRefObject, SetStateAction, useCallback } from 'react';
import { RailType } from '@shared/types/enums/rail';
import { openFloatingMenu } from '@shared/store/thunks/floatingMenu.thunk';
import { popRoute } from '@shared/store/thunks/route.thunk';
import { setFocus } from '@shared/store/slices/focus.slice';
import { store } from '@shared/store/store';
import { useAppDispatch } from '@shared/store/hooks';

/**
 * The offset for the floating menu.
 */
const FLOATING_MENU_OFFSET = 340;

interface MosaicKeyboardProps {
  colsRef: MutableRefObject<number>;
  dataRef: MutableRefObject<(Event | Setting)[]>;
  indexRef: MutableRefObject<number>;
  setIndex: Dispatch<SetStateAction<number>>;
  changeAsset: (index: number) => void;
  selectAsset: (index: number) => void;
  updateParams: () => void;
}

/**
 * Wires up the Mosaic hook's keyboard-driven navigation (grid navigation,
 * back/select, the floating menu long-press, menu re-entry). Registers its
 * own `useKeyboard` listeners; nothing here is returned or used outside this
 * hook.
 */
export default function useMosaicKeyboard({
  colsRef,
  dataRef,
  indexRef,
  setIndex,
  changeAsset,
  selectAsset,
  updateParams,
}: MosaicKeyboardProps) {
  const dispatch = useAppDispatch();

  /**
   * Handles the bounds action for the content navigation.
   * @param key - The key pressed.
   */
  const boundsAction = useCallback((key: string) => {
    switch (key) {
      case 'LEFT':
        dispatch(setFocus(Focus.MENU));
        break;
    }
  }, []);

  /**
   * Handles the key down events for the content navigation.
   * @param key - The key pressed.
   */
  const contentKeyDownHandler = useCallback((key: string) => {
    let finalIndex = indexRef.current;
    /*
     *Update the index based on the key pressed
     */
    switch (key) {
      case 'LEFT':
        setIndex((oldIndex) => {
          const col = oldIndex % colsRef.current;
          if (col <= 0) {
            boundsAction(key);
            return oldIndex;
          }
          finalIndex = oldIndex - 1;
          return finalIndex;
        });
        break;
      case 'RIGHT':
        setIndex((oldIndex) => {
          const col = oldIndex % colsRef.current;
          if (
            col >= colsRef.current - 1 ||
            oldIndex >= dataRef.current.length - 1
          ) {
            return oldIndex;
          }
          finalIndex = oldIndex + 1;
          return finalIndex;
        });
        break;
      case 'DOWN':
        setIndex((oldIndex) => {
          const newIndex = oldIndex + colsRef.current;
          if (newIndex >= dataRef.current.length) {
            return oldIndex;
          }
          finalIndex = newIndex;
          return finalIndex;
        });
        break;
      case 'UP':
        setIndex((oldIndex) => {
          const newIndex = oldIndex - colsRef.current;
          if (newIndex < 0) {
            return oldIndex;
          }
          finalIndex = newIndex;
          return newIndex;
        });
        break;
    }
    /*
     *Update the asset based on the final index
     */
    changeAsset(finalIndex);
  }, []);

  /**
   * Handles the key up events for the content navigation.
   * @param key - The key pressed.
   */
  const contentKeyUpHandler = useCallback((key: string) => {
    switch (key) {
      case 'BACK':
        const { router } = store.getState();
        if (router.route.stack.length) {
          dispatch(popRoute());
          return;
        }
        dispatch(setFocus(Focus.MENU));
        break;
      case 'SELECT':
        selectAsset(indexRef.current);
        break;
    }
  }, []);

  const contentKeyPressHandler = useCallback(() => {
    const { floatingMenu } = store.getState();
    if (dataRef.current && !floatingMenu.open) {
      if (indexRef.current >= 0) {
        // Calculate the coordinates for the floating menu
        const coordinates = {
          x: Math.min(
            Coordinates['MOSAIC'].x +
              FLOATING_MENU_OFFSET *
                Math.min(
                  indexRef.current % (colsRef.current || 1),
                  colsRef.current - 2
                ),
            Coordinates['MOSAIC_RIGHT_BORDER'].x
          ),
          y:
            indexRef.current === (colsRef.current || 1) - 1
              ? Coordinates['MOSAIC_RIGHT_BORDER'].y
              : Coordinates['MOSAIC'].y,
        };
        // Update params and open the floating menu
        updateParams();
        dispatch(
          openFloatingMenu({
            route: Routes.MAIN,
            asset: dataRef.current[indexRef.current] as Event,
            position: coordinates,
            type: RailType.REGULAR,
          })
        );
      }
    }
  }, []);

  /**
   * Handles the key down events for the menu navigation.
   * @param key - The key pressed.
   */
  const menuKeyDownHandler = useCallback((key: string) => {
    switch (key) {
      case 'RIGHT':
        dispatch(setFocus(Focus.MOSAIC));
        break;
    }
  }, []);

  /**
   * Handles the content navigation.
   * @param key - The key pressed.
   */
  useKeyboard({
    focus: Focus.MOSAIC,
    keyDownHandler: contentKeyDownHandler,
    keyUpHandler: contentKeyUpHandler,
    keyPressHandler: contentKeyPressHandler,
  });

  /**
   * Handles the menu navigation.
   * @param key - The key pressed.
   */
  useKeyboard({
    focus: Focus.MENU,
    keyDownHandler: menuKeyDownHandler,
  });
}
